import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Home, Building2, BookOpen, CalendarDays, Users, CreditCard,
  Star, Image as ImageIcon, Search, FileText, Sparkles, MessageSquare,
  Settings as SettingsIcon, LogOut, Menu, X, ShieldCheck, ToggleRight,
  Landmark, BuildingIcon, LibraryBig, Upload,
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import { platformListFeatureFlags } from "../../lib/api";

export const SIDEBAR_GROUPS = [
  {
    label: "Overview",
    items: [
      { key: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
    ],
  },
  {
    label: "Content",
    items: [
      { key: "homepage", label: "Homepage", Icon: Home },
      { key: "media",    label: "Media Library", Icon: ImageIcon },
      { key: "seo",      label: "SEO", Icon: Search },
    ],
  },
  {
    label: "Operations",
    items: [
      { key: "properties", label: "Properties", Icon: Building2 },
      { key: "bookings",   label: "Bookings",   Icon: BookOpen },
      { key: "calendar",   label: "Calendar",   Icon: CalendarDays },
      { key: "guests",     label: "Guests",     Icon: Users },
      { key: "payments",   label: "Payments",   Icon: CreditCard },
      { key: "reviews",    label: "Reviews",    Icon: Star },
    ],
  },
  {
    label: "Inquiries",
    items: [
      { key: "concierge",  label: "Concierge",  Icon: MessageSquare },
      { key: "longstay",   label: "Long Stay",  Icon: Sparkles },
      { key: "owners",     label: "Owners",     Icon: FileText },
    ],
  },
  {
    label: "System",
    items: [
      { key: "brand-settings",      label: "Brand Settings",       Icon: Landmark },
      { key: "platform-properties", label: "Platform Properties",  Icon: BuildingIcon },
      { key: "knowledge-base",      label: "Knowledge Base",       Icon: LibraryBig },
      { key: "knowledge-import",    label: "Import Knowledge",     Icon: Upload, requiresFlag: "knowledgeImportMvp" },
      { key: "concierge-shadow",    label: "Concierge Shadow",     Icon: Sparkles },
      { key: "audit",      label: "Audit Log",     Icon: ShieldCheck },
      { key: "flags",      label: "Feature Flags", Icon: ToggleRight },
      { key: "settings",   label: "Settings",      Icon: SettingsIcon },
    ],
  },
];

export default function AdminLayout({ active, onSelect, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [importFlagOn, setImportFlagOn] = useState(false);

  useEffect(() => {
    // Sprint 5 — surface the Import Knowledge nav entry only when the
    // knowledgeImportMvp flag is on for the current tenant. Fail-closed
    // (hidden) on any error.
    let alive = true;
    (async () => {
      try {
        const flags = await platformListFeatureFlags();
        const on = (flags || []).some(
          (f) => f.key === "knowledgeImportMvp" && f.enabled,
        );
        if (alive) setImportFlagOn(on);
      } catch {
        if (alive) setImportFlagOn(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const activeMeta = SIDEBAR_GROUPS.flatMap((g) => g.items).find((i) => i.key === active);

  const Sidebar = (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-7 pb-6 border-b border-[var(--ed-rule)]">
        <Link to="/" className="flex flex-col leading-none">
          <span className="font-serif text-[1.05rem] tracking-[0.18em] text-[var(--ed-ink)]">WEST LONDON</span>
          <span className="text-[var(--ed-ink-muted)] text-[0.6rem] tracking-[0.45em] uppercase mt-1.5">Stays · Studio</span>
        </Link>
        <p className="text-[0.65rem] text-[var(--ed-ink-muted)] mt-4 truncate" title={user?.email}>{user?.email}</p>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-6">
        {SIDEBAR_GROUPS.map((group) => (
          <div key={group.label}>
            <p className="px-3 mb-2 text-[0.55rem] uppercase tracking-[0.28em] text-[var(--ed-ink-muted)]">
              {group.label}
            </p>
            <div className="space-y-0.5">
              {group.items
                .filter((item) => !item.requiresFlag || (item.requiresFlag === "knowledgeImportMvp" && importFlagOn))
                .map(({ key, label, Icon }) => {
                const isActive = active === key;
                return (
                  <button
                    key={key}
                    data-testid={`admin-nav-${key}`}
                    onClick={() => { onSelect(key); setMobileOpen(false); }}
                    className={`w-full text-left px-3 py-2 flex items-center gap-3 text-sm transition-colors rounded-sm border-l-2 ${
                      isActive
                        ? "bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)] border-[var(--ed-emerald)] font-medium"
                        : "text-[var(--ed-ink-soft)] hover:bg-[var(--ed-paper)] hover:text-[var(--ed-ink)] border-transparent"
                    }`}
                  >
                    <Icon size={15} strokeWidth={1.6} className="flex-shrink-0" />
                    <span className="tracking-wide">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-[var(--ed-rule)] p-4 space-y-1.5">
        <Link
          to="/"
          data-testid="admin-view-site"
          className="block w-full text-center text-[0.65rem] uppercase tracking-[0.25em] py-2.5 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] rounded-sm transition-colors"
        >
          View Public Site
        </Link>
        <button
          data-testid="admin-logout"
          onClick={onLogout}
          className="w-full text-[0.65rem] uppercase tracking-[0.25em] py-2.5 text-[var(--ed-ink-muted)] hover:text-[var(--ed-ink)] flex items-center justify-center gap-2 transition-colors"
        >
          <LogOut size={12} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-dashboard" className="editorial min-h-screen bg-[var(--ed-bg)] text-[var(--ed-ink)]">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-40 bg-[var(--ed-bg)] border-b border-[var(--ed-rule)] px-5 py-3 flex items-center justify-between">
        <button
          data-testid="admin-mobile-toggle"
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-2 text-[var(--ed-ink)]"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        <p className="font-serif text-base tracking-wide">{activeMeta?.label || "Studio"}</p>
        <div className="w-9" />
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="relative w-72 max-w-[85vw] h-full bg-[var(--ed-bg)] shadow-xl">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 text-[var(--ed-ink)] p-1"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
            {Sidebar}
          </aside>
        </div>
      )}

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-72 xl:w-80 h-screen sticky top-0 border-r border-[var(--ed-rule)] bg-[var(--ed-bg)]">
          {Sidebar}
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 px-5 sm:px-8 lg:px-12 py-8 lg:py-12">
          <div className="max-w-[1400px] mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}
